import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AllNotifications from './AllNotifications';
import PostsShellLayout from '../support/openDiscussions/components/shell/PostsShellLayout';
import PostsMobileFloatingChrome from '../support/openDiscussions/components/shell/PostsMobileFloatingChrome';
import MessagesFollowSheet from '../MainHeader/Messaging/MessagesFollowSheet';
import { useMessagingUnreadCount } from '../MainHeader/Messaging/useMessagingUnreadCount';
import { usePostsShellLayout } from '../support/openDiscussions/hooks/usePostsShellLayout';
import { usePostsMobileScrollChrome } from '../support/openDiscussions/hooks/usePostsMobileScrollChrome';
import { isSocialMobileChromeRoute } from '../support/openDiscussions/lib/socialMobileChromeRoutes';
import { defaultHomeFeedSort } from '../support/openDiscussions';
import { buildSigninUrl, stashPendingVerificationEmail } from '../support/openDiscussions/auth/guestAuth';
import { useNotification } from '../../layout/ReusableComponent/PopupNotification/PopupNotification';
import { getStoredActiveProfileMode } from '../profileIdentity/activeProfileMode';
import '../support/openDiscussions/styles/OpenDiscussions.css';
import '../../GlobalPosts/PostsPage.css';
import './NotificationsPageShell.css';

const baseUrl = process.env.REACT_APP_BASE_URL || '';

export default function NotificationsPageShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const shell = usePostsShellLayout({
    returnTo: '/notifications',
    addNotification,
    enableWheelScroll: false,
  });
  const {
    profile,
    profileResolved,
    profileAvatarSrc,
    isMobileLayout,
    mobileDrawerOpen,
    goPosts,
    closeDrawer,
    navigate: shellNavigate,
  } = shell;

  const isEmailVerificationPending = profileResolved && profile?.emailNotVerified === true;

  useEffect(() => {
    if (!isEmailVerificationPending) return undefined;
    let cancelled = false;
    if (profile?.email) stashPendingVerificationEmail(profile.email);
    (async () => {
      try {
        await fetch(`${baseUrl}/api/signout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch {
        /* ignore */
      }
      if (!cancelled) {
        navigate(buildSigninUrl({ mode: 'signin', returnTo: '/posts' }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEmailVerificationPending, profile?.email, navigate]);

  const isSocialRoute = isSocialMobileChromeRoute(location.pathname);
  const [mobileFollowSheetOpen, setMobileFollowSheetOpen] = useState(false);
  const { hasUnread: hasUnreadMessages } = useMessagingUnreadCount(baseUrl);
  const mobileFloatingChromeEnabled = isMobileLayout && isSocialRoute && !mobileDrawerOpen;
  const mobileScrollChromeMode = usePostsMobileScrollChrome(
    mobileFloatingChromeEnabled,
    location.pathname
  );

  const openMobileHome = useCallback(() => {
    goPosts({ sort: defaultHomeFeedSort() });
    closeDrawer();
  }, [goPosts, closeDrawer]);

  const openMobileCreate = useCallback(() => {
    goPosts({ compose: '1' });
    closeDrawer();
  }, [goPosts, closeDrawer]);

  const openMobileMessages = useCallback(() => {
    navigate('/sm/all-messages');
    closeDrawer();
  }, [navigate, closeDrawer]);

  const openMobileSocial = useCallback(() => {
    setMobileFollowSheetOpen(true);
    closeDrawer();
  }, [closeDrawer]);

  const handleFollowSheetSelectUser = useCallback(
    (user) => {
      setMobileFollowSheetOpen(false);
      const uid = String(user?.userId || '').trim();
      if (!uid) return;
      navigate(`/sm/all-messages?userId=${encodeURIComponent(uid)}`);
    },
    [navigate]
  );

  const openMobileProfile = useCallback(() => {
    const uid = String(profile?.userId || '').trim();
    if (!uid) {
      shellNavigate(buildSigninUrl({ mode: 'signup', returnTo: '/notifications' }));
      closeDrawer();
      return;
    }
    const mode =
      String(profile?.profileMode || 'personal').trim().toLowerCase() === 'social' ? 'social' : 'personal';
    shellNavigate(
      `/posts?view=user-profile&userId=${encodeURIComponent(uid)}&profileMode=${encodeURIComponent(mode)}`
    );
    closeDrawer();
  }, [profile, shellNavigate, closeDrawer]);

  return (
    <>
      <PostsShellLayout
        shell={shell}
        layoutClassName="posts-page-layout--notifications"
        mainClassName="posts-page-layout__center notifications-page-shell-main"
        groupedMobileTools
        notificationsPageActive
        returnTo="/notifications"
      >
        <AllNotifications activeProfileMode={profile?.profileMode || getStoredActiveProfileMode()} />
      </PostsShellLayout>
      {isMobileLayout && isSocialRoute ? (
        <PostsMobileFloatingChrome
          chromeMode={mobileScrollChromeMode}
          hidden={mobileDrawerOpen}
          onHome={openMobileHome}
          onMessages={openMobileMessages}
          onCreate={openMobileCreate}
          onSocial={openMobileSocial}
          onProfile={openMobileProfile}
          isHomeActive={false}
          isMessagesActive={false}
          isCreateActive={false}
          isSocialActive={mobileFollowSheetOpen}
          isProfileActive={false}
          profileAvatarSrc={profileAvatarSrc}
          profileUserId={profile?.userId}
          profileDisplayName={profile?.displayName || profile?.name}
          showProfileAvatar={!!profile?.userId}
          hasUnreadMessages={hasUnreadMessages}
        />
      ) : null}
      <MessagesFollowSheet
        open={mobileFollowSheetOpen}
        onClose={() => setMobileFollowSheetOpen(false)}
        viewerUserId={profile?.userId}
        onSelectUser={handleFollowSheetSelectUser}
      />
    </>
  );
}
